/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The DR dialogs: sites, protection policies, protection groups, adding VMs, protecting a VM, and
 * a confirmation (with an optional reason) for pausing, unprotecting and deleting. Each one shows
 * the API's own answer when it refuses (a setting Swift cannot honour, a record in use...).
 */

import * as React from "react";
import styled from "styled-components";
import { Building2, Cloud, LoaderCircle, MapPin, Search, ShieldCheck, ShieldPlus, Timer, Users } from "lucide-react";

import { drErrorMessage, formatSeconds, platformName } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { T9 } from "@src/components/ui/Lists/ListTable";
import {
  CheckRow,
  FieldHint,
  FootNote,
  FootSpacer,
  FormField,
  FormGrid,
  Notice,
  OpsDialog,
  OpsInput,
  OpsSelect,
  OpsTextarea,
  RadioCards,
} from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";
import instanceSource from "@src/sources/InstanceSource";

import type { DrGroup, DrPolicy, DrSite, DrWorkload } from "@src/@types/Dr";
import type { Endpoint } from "@src/@types/Endpoint";
import type { Instance } from "@src/@types/Instance";
import type { TransferItem } from "@src/@types/MainItem";

const Spin = styled(LoaderCircle)`
  animation: dr-spin 1s linear infinite;
  @keyframes dr-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow: auto;
  padding: 2px;
`;
const SearchBox = styled.div`
  position: relative;
  svg {
    position: absolute;
    left: 10px;
    top: 10px;
    color: ${T9.slate400};
  }
  input {
    padding-left: 30px;
  }
`;
const Empty = styled.div`
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: ${T9.slate500};
`;

export const Footer = (props: {
  note?: React.ReactNode;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
  label: string;
  busyLabel: string;
  danger?: boolean;
  disabled?: boolean;
  testId?: string;
}) => (
  <>
    {props.note ? <FootNote>{props.note}</FootNote> : <FootSpacer />}
    <ActionButton variant="secondary" type="button" onClick={props.onClose} disabled={props.busy}>
      Cancel
    </ActionButton>
    <ActionButton
      type="button"
      variant={props.danger ? "danger" : "primary"}
      onClick={props.onSubmit}
      disabled={props.busy || props.disabled}
      data-test-id={props.testId}
    >
      {props.busy ? <Spin size={16} /> : null}
      {props.busy ? props.busyLabel : props.label}
    </ActionButton>
  </>
);

export const ErrorNotice = ({ error }: { error: string | null }) =>
  error ? (
    <Notice tone="danger" title="SwiftIQ refused this" style={{ marginBottom: 12 }} testId="dr-dialog-error">
      {error}
    </Notice>
  ) : null;

/** Whole minutes from a text field, or null. */
const minutes = (value: string) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// ------------------------------------------------------------------ sites

type SiteProps = {
  site?: DrSite | null;
  sites: DrSite[];
  endpoints: Endpoint[];
  onClose: () => void;
  onSaved: (site: DrSite) => void;
};
export const SiteDialog = (props: SiteProps) => {
  const editing = Boolean(props.site);
  const [name, setName] = React.useState(props.site?.name || "");
  const [kind, setKind] = React.useState<string>(props.site?.kind || "on_prem");
  const [description, setDescription] = React.useState(props.site?.description || "");
  const [endpointIds, setEndpointIds] = React.useState<string[]>(props.site?.endpoint_ids || []);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const takenBy: { [endpointId: string]: string } = {};
  props.sites
    .filter(s => s.id !== props.site?.id)
    .forEach(s => s.endpoint_ids.forEach(id => (takenBy[id] = s.name)));

  const submit = async () => {
    if (!name.trim()) {
      setError("Give the site a name.");
      return;
    }
    setBusy(true);
    setError(null);
    const values: any = { name: name.trim(), endpoint_ids: endpointIds };
    if (description.trim() || editing) values.description = description.trim() || null;
    try {
      const saved = editing
        ? await drSource.update<DrSite>("site", props.site!.id, values)
        : await drSource.create<DrSite>("site", { ...values, kind });
      props.onSaved(saved);
    } catch (err) {
      setError(drErrorMessage(err, "Could not save the site."));
      setBusy(false);
    }
  };

  return (
    <OpsDialog
      title={editing ? `Edit site ${props.site!.name}` : "New site"}
      subtitle="A place workloads run or recover in, with the SwiftIQ endpoints that reach it."
      icon={MapPin}
      onRequestClose={props.onClose}
      testId="dr-site-dialog"
      footer={
        <Footer busy={busy} onClose={props.onClose} onSubmit={submit} label={editing ? "Save" : "Create site"} busyLabel="Saving…" testId="dr-site-submit" />
      }
    >
      <ErrorNotice error={error} />
      <FormGrid columns={1}>
        <FormField label="Name" required>
          <OpsInput value={name} onChange={e => setName(e.target.value)} placeholder="Mumbai DC" data-test-id="dr-site-name" />
        </FormField>
        {editing ? null : (
          <FormField label="Kind" required as="div">
            <RadioCards
              name="dr-site-kind"
              value={kind}
              onChange={setKind}
              options={[
                { value: "on_prem", label: "On-premises", icon: Building2, description: "A data centre: Nutanix, VMware, Hyper-V…" },
                { value: "cloud", label: "Cloud", icon: Cloud, description: "A cloud account and region: AWS, Azure…" },
              ]}
            />
          </FormField>
        )}
        <FormField
          label="Endpoints"
          as="div"
          hint="A site that is the target of a protection group needs exactly one endpoint: recoveries land there. DR uses only endpoints whose credentials are in the secret store."
        >
          <ChoiceList>
            {props.endpoints.length === 0 ? <Empty>No cloud endpoints in this project.</Empty> : null}
            {props.endpoints.map(ep => (
              <CheckRow
                key={ep.id}
                checked={endpointIds.includes(ep.id)}
                disabled={Boolean(takenBy[ep.id])}
                onChange={checked =>
                  setEndpointIds(checked ? [...endpointIds, ep.id] : endpointIds.filter(id => id !== ep.id))
                }
                label={`${ep.name} · ${platformName(ep.type)}`}
                description={takenBy[ep.id] ? `In site ${takenBy[ep.id]}` : undefined}
                testId={`dr-site-endpoint-${ep.id}`}
              />
            ))}
          </ChoiceList>
        </FormField>
        <FormField label="Description">
          <OpsTextarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
        </FormField>
      </FormGrid>
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ policies

type PolicyProps = { policy?: DrPolicy | null; onClose: () => void; onSaved: (policy: DrPolicy) => void };
export const PolicyDialog = (props: PolicyProps) => {
  const p = props.policy;
  const editing = Boolean(p);
  const [name, setName] = React.useState(p?.name || "");
  const [rpo, setRpo] = React.useState(String(p ? Math.round(p.rpo_seconds / 60) : 15));
  const [interval, setIntervalText] = React.useState(p ? String(Math.round(p.sync_interval_seconds / 60)) : "");
  const [mode, setMode] = React.useState<string>(p?.replication_mode || "scheduled");
  const [bandwidth, setBandwidth] = React.useState(p?.bandwidth?.limit_mbps ? String(p.bandwidth.limit_mbps) : "");
  const [consistency, setConsistency] = React.useState<string>(p?.consistency || "crash");
  const [requireApp, setRequireApp] = React.useState(Boolean(p?.require_application_consistency));
  const [points, setPoints] = React.useState(String(p?.retention?.restore_points || 3));
  const [immutable, setImmutable] = React.useState(Boolean(p?.immutable));
  const [lockDays, setLockDays] = React.useState(String(p?.retention?.immutable_days || 30));
  const [lockMode, setLockMode] = React.useState<string>(p?.retention?.immutable_mode || "governance");
  const [description, setDescription] = React.useState(p?.description || "");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const rpoMinutes = minutes(rpo);
  const intervalMinutes = interval.trim() ? minutes(interval) : rpoMinutes;
  const pointsN = Number(points);
  const lockDaysN = Number(lockDays);
  const nearContinuous = mode === "near_continuous";
  const bandwidthN = bandwidth.trim() ? Number(bandwidth) : null;
  const problem = !name.trim()
    ? "Give the policy a name."
    : !rpoMinutes
      ? "The RPO is a whole number of minutes."
      : nearContinuous && rpoMinutes < 10
        ? "Near-continuous replication needs an RPO of 10 minutes or more: a sync itself takes minutes."
      : bandwidthN !== null && !(bandwidthN > 0)
        ? "The bandwidth limit is a number of Mbit/s above 0, or empty for none."
      : !intervalMinutes
        ? "Syncs run every whole number of minutes."
        : intervalMinutes > rpoMinutes
          ? "Syncs must run at least as often as the RPO, or it can never be met."
          : !(Number.isInteger(pointsN) && pointsN >= 1 && pointsN <= 50)
            ? "Keep from 1 to 50 restore points."
          : immutable && !(Number.isInteger(lockDaysN) && lockDaysN >= 1 && lockDaysN <= 36500)
            ? "Lock restore points for a whole number of days, from 1 to 36500."
            : null;

  const submit = async () => {
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const values: any = {
      name: name.trim(),
      rpo_seconds: rpoMinutes! * 60,
      sync_interval_seconds: intervalMinutes! * 60,
      replication_mode: mode,
      bandwidth: bandwidthN ? { limit_mbps: bandwidthN } : null,
      consistency,
      require_application_consistency: consistency === "application" && requireApp,
      retention: immutable
        ? { restore_points: pointsN, immutable_days: lockDaysN, immutable_mode: lockMode }
        : { restore_points: pointsN },
      immutable,
    };
    if (description.trim() || editing) values.description = description.trim() || null;
    try {
      const saved = editing
        ? await drSource.update<DrPolicy>("policy", p!.id, values)
        : await drSource.create<DrPolicy>("policy", values);
      props.onSaved(saved);
    } catch (err) {
      setError(drErrorMessage(err, "Could not save the policy."));
      setBusy(false);
    }
  };

  return (
    <OpsDialog
      title={editing ? `Edit policy ${p!.name}` : "New protection policy"}
      subtitle="How often protected VMs sync, how much data a recovery may lose (RPO), and what is kept."
      icon={Timer}
      onRequestClose={props.onClose}
      testId="dr-policy-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={editing ? "Save" : "Create policy"}
          busyLabel="Saving…"
          note={
            nearContinuous
              ? "Near-continuous (Level 2): incremental syncs back to back. Not continuous data protection."
              : "Scheduled syncs (Level 1): this is not continuous data protection."
          }
          testId="dr-policy-submit"
        />
      }
    >
      <ErrorNotice error={error} />
      <FormGrid columns={2}>
        <FormField label="Name" required span={2}>
          <OpsInput value={name} onChange={e => setName(e.target.value)} placeholder="Gold, 15 minutes" data-test-id="dr-policy-name" />
        </FormField>
        <FormField label="RPO (minutes)" required hint="The most data a recovery may lose.">
          <OpsInput value={rpo} onChange={e => setRpo(e.target.value)} inputMode="numeric" data-test-id="dr-policy-rpo" />
        </FormField>
        {nearContinuous ? (
          <FormField label="Sync every" hint="Each sync starts as soon as the last one ends.">
            <OpsInput value="Back to back" disabled readOnly />
          </FormField>
        ) : (
          <FormField label="Sync every (minutes)" hint={`Empty: every ${rpoMinutes ? formatSeconds(rpoMinutes * 60) : "RPO"}.`}>
            <OpsInput value={interval} onChange={e => setIntervalText(e.target.value)} inputMode="numeric" placeholder={rpo} data-test-id="dr-policy-interval" />
          </FormField>
        )}
        <FormField label="Replication" required span={2} as="div">
          <RadioCards
            name="dr-policy-mode"
            value={mode}
            onChange={setMode}
            options={[
              { value: "scheduled", label: "Scheduled syncs", description: "A sync every interval (Level 1)." },
              {
                value: "near_continuous",
                label: "Near-continuous",
                description:
                  "The next sync starts as soon as the last one ends (Level 2), for RPOs of 10 minutes or more. The achieved RPO is about twice a sync's duration; SwiftIQ warns when a sync makes the RPO unreachable.",
              },
            ]}
          />
        </FormField>
        <FormField label="Bandwidth limit (Mbit/s)" hint="Empty: no limit. Applies to transfers this policy creates.">
          <OpsInput value={bandwidth} onChange={e => setBandwidth(e.target.value)} inputMode="decimal" placeholder="No limit" data-test-id="dr-policy-bandwidth" />
        </FormField>
        <FormField label="Consistency" required span={2} as="div">
          <RadioCards
            name="dr-policy-consistency"
            value={consistency}
            onChange={setConsistency}
            options={[
              { value: "crash", label: "Crash-consistent", description: "Disks as they are at the snapshot, like after a power cut." },
              {
                value: "application",
                label: "Application-consistent",
                description: "The guest is quiesced first (VSS on Windows, a filesystem freeze on Linux). A sync falls back to crash-consistent, with a warning, when it cannot.",
              },
            ]}
          />
        </FormField>
        {consistency === "application" ? (
          <FormField label="" span={2} as="div">
            <CheckRow
              checked={requireApp}
              onChange={setRequireApp}
              label="Require application consistency"
              description="A sync that cannot quiesce the guest fails instead of falling back."
            />
          </FormField>
        ) : null}
        <FormField label="Restore points kept" required hint="How many recent restore points the DR site keeps for point-in-time recovery.">
          <OpsInput value={points} onChange={e => setPoints(e.target.value)} inputMode="numeric" data-test-id="dr-policy-points" />
        </FormField>
        <FormField label="" span={2} as="div">
          <CheckRow
            checked={immutable}
            onChange={setImmutable}
            label="Immutable restore points"
            description="Each restore point is locked at the DR site so no one can delete it before the lock ends, e.g. after a ransomware attack. AWS DR sites only (EBS snapshot lock); groups on other sites are refused."
            testId="dr-policy-immutable"
          />
        </FormField>
        {immutable ? (
          <>
            <FormField label="Locked for (days)" required hint="Retention deletes a restore point only after its lock ends.">
              <OpsInput value={lockDays} onChange={e => setLockDays(e.target.value)} inputMode="numeric" data-test-id="dr-policy-lock-days" />
            </FormField>
            <FormField label="Lock mode" required span={2} as="div">
              <RadioCards
                name="dr-policy-lock-mode"
                value={lockMode}
                onChange={setLockMode}
                options={[
                  { value: "governance", label: "Governance", description: "Accounts allowed to unlock snapshots (ec2:UnlockSnapshot) can still remove a lock early." },
                  { value: "compliance", label: "Compliance", description: "No one can remove the lock or shorten it, not even the AWS account's root user. Choose carefully: the snapshots are billed until the lock ends." },
                ]}
              />
            </FormField>
          </>
        ) : null}
        <FormField label="Description" span={2}>
          <OpsTextarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
        </FormField>
      </FormGrid>
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ protection groups

type GroupProps = {
  group?: DrGroup | null;
  sites: DrSite[];
  policies: DrPolicy[];
  onClose: () => void;
  onSaved: (group: DrGroup) => void;
};
export const GroupDialog = (props: GroupProps) => {
  const g = props.group;
  const editing = Boolean(g);
  const [name, setName] = React.useState(g?.name || "");
  const [policyId, setPolicyId] = React.useState(g?.policy_id || props.policies[0]?.id || "");
  const [sourceId, setSourceId] = React.useState(g?.source_site_id || "");
  const [targetId, setTargetId] = React.useState(g?.target_site_id || "");
  const [description, setDescription] = React.useState(g?.description || "");
  const [consistent, setConsistent] = React.useState(Boolean(g?.consistency_group));
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const siteLabel = (s: DrSite) =>
    `${s.name} (${s.endpoints.map(e => platformName(e.type)).join(", ") || "no endpoints"})`;

  const submit = async () => {
    const problem = !name.trim()
      ? "Give the group a name."
      : !policyId
        ? "Choose a protection policy (create one first)."
        : !editing && (!sourceId || !targetId)
          ? "Choose where the VMs run and where they recover."
          : !editing && sourceId === targetId
            ? "The source and target sites must be different."
            : null;
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const values: any = { name: name.trim(), policy_id: policyId, consistency_group: consistent };
    if (description.trim() || editing) values.description = description.trim() || null;
    try {
      const saved = editing
        ? await drSource.update<DrGroup>("protection_group", g!.id, values)
        : await drSource.create<DrGroup>("protection_group", { ...values, source_site_id: sourceId, target_site_id: targetId });
      props.onSaved(saved);
    } catch (err) {
      setError(drErrorMessage(err, "Could not save the protection group."));
      setBusy(false);
    }
  };

  return (
    <OpsDialog
      title={editing ? `Edit group ${g!.name}` : "New protection group"}
      subtitle="VMs protected by one policy, from a source site to a DR site. SwiftIQ checks what the two can do together."
      icon={Users}
      onRequestClose={props.onClose}
      testId="dr-group-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={editing ? "Save" : "Create group"}
          busyLabel={editing ? "Saving…" : "Checking the sites…"}
          testId="dr-group-submit"
        />
      }
    >
      <ErrorNotice error={error} />
      <FormGrid columns={2}>
        <FormField label="Name" required span={2}>
          <OpsInput value={name} onChange={e => setName(e.target.value)} placeholder="ERP to AWS" data-test-id="dr-group-name" />
        </FormField>
        <FormField label="Protection policy" required span={2}>
          <OpsSelect value={policyId} onChange={e => setPolicyId(e.target.value)} data-test-id="dr-group-policy">
            <option value="">Choose…</option>
            {props.policies.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} · RPO {formatSeconds(p.rpo_seconds)}
              </option>
            ))}
          </OpsSelect>
        </FormField>
        <FormField label="VMs run in" required>
          <OpsSelect value={sourceId} disabled={editing} onChange={e => setSourceId(e.target.value)} data-test-id="dr-group-source">
            <option value="">Choose a site…</option>
            {props.sites.map(s => (
              <option key={s.id} value={s.id}>
                {siteLabel(s)}
              </option>
            ))}
          </OpsSelect>
        </FormField>
        <FormField label="They recover in" required>
          <OpsSelect value={targetId} disabled={editing} onChange={e => setTargetId(e.target.value)} data-test-id="dr-group-target">
            <option value="">Choose a site…</option>
            {props.sites.map(s => (
              <option key={s.id} value={s.id}>
                {siteLabel(s)}
              </option>
            ))}
          </OpsSelect>
        </FormField>
        {editing ? (
          <FieldHint style={{ gridColumn: "span 2" }}>The sites of a group cannot change: create another group instead.</FieldHint>
        ) : null}
        <FormField label="Description" span={2}>
          <OpsTextarea value={description} onChange={e => setDescription(e.target.value)} rows={2} />
        </FormField>
        <FormField label="Consistency group" span={2}>
          <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input
              type="checkbox"
              checked={consistent}
              onChange={e => setConsistent(e.target.checked)}
              data-test-id="dr-group-consistency"
            />
            <span>
              Sync all VMs of the group together, from one snapshot taken at the same instant, so they recover to the
              same point (an application spread over several VMs). Their VMs must run on one endpoint; Nutanix and AWS
              sources take such snapshots.
            </span>
          </label>
        </FormField>
      </FormGrid>
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ adding VMs

type AddProps = {
  groups: DrGroup[];
  sites: DrSite[];
  workloads: DrWorkload[];
  initialGroupId?: string | null;
  onClose: () => void;
  onAdded: (added: DrWorkload[]) => void;
};
export const AddWorkloadsDialog = (props: AddProps) => {
  const [groupId, setGroupId] = React.useState(props.initialGroupId || props.groups[0]?.id || "");
  const group = props.groups.find(g => g.id === groupId);
  const sourceSite = props.sites.find(s => s.id === group?.source_site_id);
  const endpoints = sourceSite?.endpoints || [];
  const [endpointId, setEndpointId] = React.useState("");
  const [instances, setInstances] = React.useState<Instance[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState("");
  const [selected, setSelected] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setEndpointId(endpoints.length === 1 ? endpoints[0].id : "");
    setSelected([]);
  }, [groupId]);

  React.useEffect(() => {
    if (!endpointId) {
      setInstances(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    instanceSource
      .loadInstances(endpointId, true)
      .then(list => {
        if (!cancelled) setInstances(list || []);
      })
      .catch(err => {
        if (!cancelled) setError(drErrorMessage(err, "Could not list the VMs of this endpoint."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [endpointId]);

  const protectedIds = new Set(props.workloads.filter(w => w.source_endpoint_id === endpointId).map(w => w.instance));
  const shown = (instances || []).filter(
    i => !filter.trim() || `${i.name} ${i.id} ${i.os_type}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  const submit = async () => {
    if (!group || !endpointId || !selected.length) {
      setError("Choose the group, the endpoint and at least one VM.");
      return;
    }
    setBusy(true);
    setError(null);
    const added: DrWorkload[] = [];
    const failed: string[] = [];
    for (const id of selected) {
      try {
        added.push(await drSource.create<DrWorkload>("workload", { group_id: group.id, source_endpoint_id: endpointId, instance: id }));
      } catch (err) {
        const vm = instances?.find(i => i.id === id);
        failed.push(`${vm?.name || id}: ${drErrorMessage(err, "refused")}`);
      }
    }
    if (failed.length) {
      setError(failed.join("\n"));
      setBusy(false);
      setSelected(selected.filter(id => !added.some(w => w.instance === id)));
      if (added.length) props.onAdded(added);
      return;
    }
    props.onAdded(added);
  };

  return (
    <OpsDialog
      title="Add VMs to protect"
      subtitle="Choose VMs on the group's source site. They are added unprotected: protect each one when its replication is ready."
      icon={ShieldPlus}
      width={720}
      onRequestClose={props.onClose}
      testId="dr-add-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={selected.length ? `Add ${selected.length} VM${selected.length > 1 ? "s" : ""}` : "Add VMs"}
          busyLabel="Adding…"
          disabled={!selected.length}
          testId="dr-add-submit"
        />
      }
    >
      {error ? (
        <Notice tone="danger" title="SwiftIQ refused some of this" style={{ marginBottom: 12, whiteSpace: "pre-line" }}>
          {error}
        </Notice>
      ) : null}
      <FormGrid columns={2}>
        <FormField label="Protection group" required>
          <OpsSelect value={groupId} onChange={e => setGroupId(e.target.value)} data-test-id="dr-add-group">
            {props.groups.map(g => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </OpsSelect>
        </FormField>
        <FormField label="Source endpoint" required>
          <OpsSelect value={endpointId} onChange={e => setEndpointId(e.target.value)} data-test-id="dr-add-endpoint">
            <option value="">Choose…</option>
            {endpoints.map(ep => (
              <option key={ep.id} value={ep.id}>
                {ep.name} · {platformName(ep.type)}
              </option>
            ))}
          </OpsSelect>
        </FormField>
        <FormField label="VMs" span={2} as="div" aside={instances ? `${selected.length} of ${instances.length} chosen` : undefined}>
          <SearchBox>
            <Search size={14} />
            <OpsInput value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search by name, ID or OS" />
          </SearchBox>
          <ChoiceList style={{ marginTop: 8 }}>
            {loading ? (
              <Empty>
                <Spin size={14} /> Listing the VMs…
              </Empty>
            ) : null}
            {!loading && instances && !shown.length ? <Empty>No VMs match.</Empty> : null}
            {!loading && !endpointId ? <Empty>Choose the endpoint the VMs run on.</Empty> : null}
            {!loading
              ? shown.map(i => (
                  <CheckRow
                    key={i.id}
                    checked={selected.includes(i.id)}
                    disabled={protectedIds.has(i.id)}
                    onChange={checked => setSelected(checked ? [...selected, i.id] : selected.filter(x => x !== i.id))}
                    label={i.name}
                    description={
                      protectedIds.has(i.id)
                        ? "Already in disaster recovery"
                        : `${i.os_type || "unknown OS"} · ${i.num_cpu || "?"} vCPU · ${i.memory_mb ? Math.round(i.memory_mb / 1024) : "?"} GiB`
                    }
                    testId={`dr-add-vm-${i.id}`}
                  />
                ))
              : null}
          </ChoiceList>
        </FormField>
      </FormGrid>
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ protecting a VM

type ProtectProps = {
  workload: DrWorkload;
  group: DrGroup | undefined;
  targetEndpoint: { id: string; name: string | null; type: string | null } | undefined;
  transfers: TransferItem[];
  onClose: () => void;
  onProtected: (workload: DrWorkload) => void;
  onOpenWizard: () => void;
};
export const ProtectDialog = (props: ProtectProps) => {
  const w = props.workload;
  const candidates = props.transfers.filter(
    t =>
      t.scenario === "replica" &&
      t.origin_endpoint_id === w.source_endpoint_id &&
      t.destination_endpoint_id === props.targetEndpoint?.id &&
      (t.instances || []).length === 1 &&
      [w.instance, w.name].includes(t.instances[0]),
  );
  const [transferId, setTransferId] = React.useState(candidates[0]?.id || "");
  const [paused, setPaused] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async () => {
    if (!transferId) return;
    setBusy(true);
    setError(null);
    try {
      props.onProtected(await drSource.workloadAction(w.id, "protect", { transfer_id: transferId, paused }));
    } catch (err) {
      setError(drErrorMessage(err, "Could not protect the VM."));
      setBusy(false);
    }
  };

  return (
    <OpsDialog
      title={`Protect ${w.name}`}
      subtitle={`Replicate it to ${props.targetEndpoint?.name || "the DR site"} (${platformName(props.targetEndpoint?.type)}) with the ${props.group?.name || "group"}'s policy.`}
      icon={ShieldCheck}
      tone="success"
      onRequestClose={props.onClose}
      testId="dr-protect-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={paused ? "Protect (paused)" : "Protect and sync now"}
          busyLabel="Starting…"
          disabled={!transferId}
          note="SwiftIQ starts each sync when the policy's interval has passed since the previous one."
          testId="dr-protect-submit"
        />
      }
    >
      <ErrorNotice error={error} />
      {candidates.length ? (
        <FormGrid columns={1}>
          <FormField label="Replication (a replica transfer of this VM)" required as="div">
            <RadioCards
              name="dr-protect-transfer"
              value={transferId}
              onChange={setTransferId}
              options={candidates.map(t => ({
                value: t.id,
                label: t.notes || t.id,
                description: `Last run: ${(t.last_execution_status || "never").toLowerCase()} · updated ${new Date(t.updated_at).toLocaleString()}`,
              }))}
            />
          </FormField>
          <CheckRow
            checked={paused}
            onChange={setPaused}
            label="Start paused"
            description="Protection is set up, but no sync starts until it is resumed."
          />
        </FormGrid>
      ) : (
        <Notice
          tone="info"
          title="This VM has no replica transfer to the DR site yet"
          actions={
            <ActionButton type="button" variant="secondary" onClick={props.onOpenWizard}>
              Open the transfer wizard
            </ActionButton>
          }
        >
          Create a replica transfer of {w.name} to {props.targetEndpoint?.name || "the DR site's endpoint"} with the wizard
          (target network, options), then come back and protect the VM with it. SwiftIQ then keeps it synced to the policy.
        </Notice>
      )}
    </OpsDialog>
  );
};

// ------------------------------------------------------------------ confirmations

type ConfirmProps = {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  busyLabel: string;
  danger?: boolean;
  /** Ask for a reason (kept in the history). */
  reason?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
};
export const ConfirmDialog = (props: ConfirmProps) => {
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.onConfirm(reason.trim());
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ refused this."));
      setBusy(false);
    }
  };
  return (
    <OpsDialog
      title={props.title}
      tone={props.danger ? "danger" : undefined}
      onRequestClose={props.onClose}
      width={520}
      testId="dr-confirm-dialog"
      footer={
        <Footer
          busy={busy}
          onClose={props.onClose}
          onSubmit={submit}
          label={props.confirmLabel}
          busyLabel={props.busyLabel}
          danger={props.danger}
          testId="dr-confirm-submit"
        />
      }
    >
      <ErrorNotice error={error} />
      <div style={{ fontSize: 14, lineHeight: "20px", color: T9.slate700 }}>{props.message}</div>
      {props.reason ? (
        <FormGrid columns={1} style={{ marginTop: 12 }}>
          <FormField label="Reason" hint="Kept in the replication's history and the audit log.">
            <OpsTextarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={500} data-test-id="dr-confirm-reason" />
          </FormField>
        </FormGrid>
      ) : null}
    </OpsDialog>
  );
};
